import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSecureAuth } from '../contexts/SecureAuthContext';
import { apiService, API_ENDPOINTS } from '../config/api';
import { SupportService } from '../services/supportService';
import type {
  SupportFormData,
  SupportScreenshot,
  SupportRequestPayload,
} from '../types';

const DRAFT_KEY = 'studysync_support_draft';
const DEBOUNCE_MS = 500;

const INITIAL_FORM_DATA: SupportFormData = {
  category: null,
  description: '',
  urgency: false,
  browserDevice: '',
  studyId: '',
  studyName: '',
  participantsAffected: '',
  errorMessage: '',
  participantIdentifier: '',
  enrollmentStep: '',
  additionalNotes: '',
  screenshots: [],
};

function parseBrowserInfo(): string {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  let os = 'Unknown';

  if (ua.includes('Firefox/')) browser = 'Firefox';
  else if (ua.includes('Edg/')) browser = 'Edge';
  else if (ua.includes('Chrome/')) browser = 'Chrome';
  else if (ua.includes('Safari/') && !ua.includes('Chrome')) browser = 'Safari';

  if (ua.includes('Mac OS X')) os = 'macOS';
  else if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

  return `${browser} on ${os}`;
}

function compressImage(file: File, maxWidth = 1200, quality = 0.8): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ratio = Math.min(maxWidth / img.width, 1);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context unavailable'));
          return;
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(dataUrl.split(',')[1]);
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export type SubmitStatus = 'idle' | 'success' | 'error';

export interface UseSupportFormReturn {
  formData: SupportFormData;
  setField: <K extends keyof SupportFormData>(key: K, value: SupportFormData[K]) => void;
  resetCategory: () => void;
  isSubmitting: boolean;
  submitStatus: SubmitStatus;
  error: string | null;
  submitForm: () => Promise<void>;
  hasDraft: boolean;
  restoreDraft: () => void;
  dismissDraft: () => void;
  resetForm: () => void;
  detectedStudyId: string;
  detectedStudyName: string;
  addScreenshots: (files: File[]) => void;
  removeScreenshot: (id: string) => void;
  browserInfo: string;
}

export function useSupportForm(): UseSupportFormReturn {
  const { user } = useSecureAuth();
  const location = useLocation();

  const [formData, setFormData] = useState<SupportFormData>(INITIAL_FORM_DATA);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [hasDraft, setHasDraft] = useState(false);
  const [detectedStudyId, setDetectedStudyId] = useState('');
  const [detectedStudyName, setDetectedStudyName] = useState('');
  const [browserInfo] = useState(() => parseBrowserInfo());

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftCheckedRef = useRef(false);

  // Auto-detect studyId from URL
  useEffect(() => {
    const match = location.pathname.match(/\/researcher\/study\/([^/]+)/);
    const urlStudyId = match ? match[1] : '';

    if (urlStudyId && urlStudyId !== detectedStudyId) {
      setDetectedStudyId(urlStudyId);
      // Fetch study name
      apiService.get(API_ENDPOINTS.STUDIES.GET(urlStudyId))
        .then((res: any) => {
          const name = res?.study?.name || res?.name || res?.data?.name || '';
          setDetectedStudyName(name);
          setFormData((prev) => ({
            ...prev,
            studyId: urlStudyId,
            studyName: name,
          }));
        })
        .catch(() => {
          setDetectedStudyName('');
        });
    } else if (!urlStudyId) {
      setDetectedStudyId('');
      setDetectedStudyName('');
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check for saved draft on mount
  useEffect(() => {
    if (draftCheckedRef.current) return;
    draftCheckedRef.current = true;

    try {
      const saved = sessionStorage.getItem(DRAFT_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.category || parsed.description) {
          setHasDraft(true);
        }
      }
    } catch {
      // ignore
    }
  }, []);

  // Debounced save to sessionStorage
  const saveDraft = useCallback((data: SupportFormData) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      try {
        // Don't save screenshots to sessionStorage (too large)
        const { screenshots, ...saveable } = data;
        sessionStorage.setItem(DRAFT_KEY, JSON.stringify(saveable));
      } catch {
        // sessionStorage full or unavailable
      }
    }, DEBOUNCE_MS);
  }, []);

  const setField = useCallback(<K extends keyof SupportFormData>(key: K, value: SupportFormData[K]) => {
    setFormData((prev) => {
      const next = { ...prev, [key]: value };
      saveDraft(next);
      return next;
    });
  }, [saveDraft]);

  const resetCategory = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      category: null,
    }));
    setSubmitStatus('idle');
    setError(null);
  }, []);

  const restoreDraft = useCallback(() => {
    try {
      const saved = sessionStorage.getItem(DRAFT_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setFormData((prev) => ({
          ...prev,
          ...parsed,
          screenshots: prev.screenshots, // keep current screenshots
          // Update auto-detected study if on a study page
          studyId: detectedStudyId || parsed.studyId || '',
          studyName: detectedStudyName || parsed.studyName || '',
        }));
      }
    } catch {
      // ignore
    }
    setHasDraft(false);
  }, [detectedStudyId, detectedStudyName]);

  const dismissDraft = useCallback(() => {
    sessionStorage.removeItem(DRAFT_KEY);
    setHasDraft(false);
  }, []);

  const resetForm = useCallback(() => {
    setFormData({
      ...INITIAL_FORM_DATA,
      studyId: detectedStudyId,
      studyName: detectedStudyName,
    });
    setSubmitStatus('idle');
    setError(null);
    setIsSubmitting(false);
    sessionStorage.removeItem(DRAFT_KEY);
    setHasDraft(false);
  }, [detectedStudyId, detectedStudyName]);

  const addScreenshots = useCallback((files: File[]) => {
    setFormData((prev) => {
      const remaining = 3 - prev.screenshots.length;
      const toAdd = files.slice(0, remaining);
      const newScreenshots: SupportScreenshot[] = toAdd.map((file) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        preview: URL.createObjectURL(file),
      }));
      return {
        ...prev,
        screenshots: [...prev.screenshots, ...newScreenshots],
      };
    });
  }, []);

  const removeScreenshot = useCallback((id: string) => {
    setFormData((prev) => {
      const removed = prev.screenshots.find((s) => s.id === id);
      if (removed) URL.revokeObjectURL(removed.preview);
      return {
        ...prev,
        screenshots: prev.screenshots.filter((s) => s.id !== id),
      };
    });
  }, []);

  const submitForm = useCallback(async () => {
    if (!formData.category || !formData.description.trim()) return;
    if (!user) return;

    setIsSubmitting(true);
    setError(null);
    setSubmitStatus('idle');

    try {
      // Compress screenshots
      const compressedScreenshots = await Promise.all(
        formData.screenshots.map(async (s) => {
          try {
            const data = s.compressed || await compressImage(s.file);
            return {
              filename: s.file.name || 'screenshot.jpg',
              data,
              mimeType: 'image/jpeg' as const,
            };
          } catch {
            return null;
          }
        })
      );

      const payload: SupportRequestPayload = {
        category: formData.category,
        description: formData.description.trim(),
        urgency: formData.urgency,
        researcherName: user.displayName || 'Unknown Researcher',
        researcherEmail: user.email || '',
        browserInfo,
      };

      if (formData.browserDevice) payload.browserDevice = formData.browserDevice;
      if (formData.studyId) payload.studyId = formData.studyId;
      if (formData.studyName) payload.studyName = formData.studyName;
      if (formData.participantsAffected) payload.participantsAffected = parseInt(formData.participantsAffected, 10);
      if (formData.errorMessage) payload.errorMessage = formData.errorMessage;
      if (formData.participantIdentifier) payload.participantIdentifier = formData.participantIdentifier;
      if (formData.enrollmentStep) payload.enrollmentStep = formData.enrollmentStep;
      if (formData.additionalNotes) payload.additionalNotes = formData.additionalNotes;

      const validScreenshots = compressedScreenshots.filter(Boolean) as SupportRequestPayload['screenshots'];
      if (validScreenshots && validScreenshots.length > 0) {
        payload.screenshots = validScreenshots;
      }

      await SupportService.submitSupportRequest(payload);

      setSubmitStatus('success');
      sessionStorage.removeItem(DRAFT_KEY);
      setHasDraft(false);
    } catch (err: any) {
      setSubmitStatus('error');
      setError(err.message || 'Failed to submit support request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, user, browserInfo]);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      formData.screenshots.forEach((s) => URL.revokeObjectURL(s.preview));
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    formData,
    setField,
    resetCategory,
    isSubmitting,
    submitStatus,
    error,
    submitForm,
    hasDraft,
    restoreDraft,
    dismissDraft,
    resetForm,
    detectedStudyId,
    detectedStudyName,
    addScreenshots,
    removeScreenshot,
    browserInfo,
  };
}
